import { sharepointApi } from "./api";
import { UPLOAD_CHUNK_SIZE } from "./constants";

type OnProgress = (progress: number) => void;

/**
 * PUTs one chunk to a Graph upload session.
 *
 * The session URL is pre-authorized by Graph, so this deliberately sends **no**
 * `Authorization` header — the bytes go browser → Microsoft and never through
 * the BFF. Every PUT must carry `Content-Range`, even a single-chunk upload of
 * a small file; Graph answers `400 invalidRequest` without it.
 *
 * XHR rather than `fetch` because only XHR reports upload progress.
 */
const putChunk = (
  uploadUrl: string,
  chunk: Blob,
  start: number,
  totalSize: number,
  onChunkProgress: (loadedBytes: number) => void
) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) onChunkProgress(event.loaded);
    });
    xhr.addEventListener("load", () => {
      // 202 = chunk accepted, more expected; 200/201 = final chunk, item created.
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      // `statusText` is empty over HTTP/2 and Graph puts the real reason in
      // the body, so surface that.
      const reason = xhr.responseText || xhr.statusText || "unknown error";
      reject(new Error(`Upload failed (${xhr.status}): ${reason}`));
    });
    xhr.addEventListener("error", () =>
      reject(new Error("Upload failed: network error"))
    );
    xhr.addEventListener("abort", () => reject(new Error("Upload aborted")));

    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader(
      "Content-Range",
      `bytes ${start}-${start + chunk.size - 1}/${totalSize}`
    );
    xhr.send(chunk);
  });

/**
 * Uploads `file` into `folderId`. One path for every file size: the BFF opens
 * an upload session, then the file goes to it in `UPLOAD_CHUNK_SIZE` chunks.
 */
export const uploadFile = async (
  file: File,
  folderId: string,
  onProgress: OnProgress
) => {
  // There is no valid `Content-Range` for zero bytes, so Graph would 400.
  if (file.size === 0) {
    throw new Error("File is empty and cannot be uploaded");
  }

  const { uploadUrl } = await sharepointApi.createUploadSession(
    folderId,
    file.name
  );
  if (!uploadUrl) {
    throw new Error("Upload session did not return an upload URL");
  }

  for (let start = 0; start < file.size; start += UPLOAD_CHUNK_SIZE) {
    const end = Math.min(start + UPLOAD_CHUNK_SIZE, file.size);
    await putChunk(
      uploadUrl,
      file.slice(start, end),
      start,
      file.size,
      (loadedBytes) =>
        onProgress(Math.round(((start + loadedBytes) / file.size) * 100))
    );
  }
};
